// Per-battle registry of DOM anchors, keyed by stable string. The
// View Transitions driver and the combat-lunge caller both look up
// elements by these keys; components register themselves from a
// ref callback. The map is mutation-only — register/unregister never
// re-renders.

import { createContext, useContext } from "react";

export type BoardRefKey = string;

export interface BoardRefs {
  register(key: BoardRefKey, el: HTMLElement | null): void;
  unregister(key: BoardRefKey): void;
  get(key: BoardRefKey): HTMLElement | null;
  getRect(key: BoardRefKey): DOMRect | null;
}

export function createBoardRefs(): BoardRefs {
  const map = new Map<BoardRefKey, HTMLElement>();
  return {
    register(key, el) {
      if (el == null) {
        map.delete(key);
      } else {
        map.set(key, el);
      }
    },
    unregister(key) {
      map.delete(key);
    },
    get(key) {
      return map.get(key) ?? null;
    },
    getRect(key) {
      const el = map.get(key);
      if (!el) return null;
      return el.getBoundingClientRect();
    },
  };
}

export function cardKey(entityId: number): BoardRefKey {
  return `card:${entityId}`;
}

/** Inner FX wrapper for a battlefield card. Distinct from `cardKey`
 *  so combat lunge can run on it without fighting the outer's static
 *  `combatLift` transform. */
export function cardFxKey(entityId: number): BoardRefKey {
  return `card-fx:${entityId}`;
}

export function deckKey(seat: number): BoardRefKey {
  return `deck:${seat}`;
}

export function graveKey(seat: number): BoardRefKey {
  return `grave:${seat}`;
}

export function exileKey(seat: number): BoardRefKey {
  return `exile:${seat}`;
}

const BoardRefsContext = createContext<BoardRefs | null>(null);

export const BoardRefsProvider = BoardRefsContext.Provider;

// No-op fallback for tests / sandbox renders mounting a Card without
// a provider. Production always wraps in <BoardRefsProvider>.
const NOOP_REFS: BoardRefs = {
  register() {},
  unregister() {},
  get() {
    return null;
  },
  getRect() {
    return null;
  },
};

export function useBoardRefs(): BoardRefs {
  const ctx = useContext(BoardRefsContext);
  return ctx ?? NOOP_REFS;
}
